import { c as _c } from "react/compiler-runtime";
import type { Address, BigIntish, ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { DataList } from '@morpho-org/ui-v6/client';
import { Icon } from '@repo/ui';
import { Timelock } from '@repo/ui/icons';
import { memo, useMemo } from 'react';
import type { ApiVaultWarning } from '@/helpers/warningTypes';
import { WarningIcon } from '@/components/common/WarningIcon';
import { formatPendingActionsLabel, VaultPendingTimelockInfo } from '@/components/VaultPage/components/VaultPendingTimelockInfo';
import { formatPreciseDuration } from '@/components/VaultPage/helpers/timelock';
import { apiWarningLevelToEnum, VaultWarningType } from '@/helpers/warnings';
import type { VaultPendingTimelocks as VaultPendingTimelocksType } from './types';
import VaultPendingTimelocks from './VaultPendingTimelocks';
interface TimelockDurationDataLineProps {
  chainId: ChainId;
  timelock?: BigIntish;
  address?: Address;
  pendingTimelocks: VaultPendingTimelocksType;
  warnings?: ApiVaultWarning[];
}
const TimelockDurationDataLine = t0 => {
  const $ = _c(21);
  const {
    timelock,
    chainId,
    pendingTimelocks,
    address,
    warnings
  } = t0;
  let t1;
  if ($[0] !== timelock) {
    t1 = isDefined(timelock) ? formatPreciseDuration(timelock) : null;
    $[0] = timelock;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timelockDuration = t1;
  const hasPendingTimelocks = pendingTimelocks.length > 0;
  let t2;
  if ($[2] !== warnings) {
    bb0: {
      const warning = warnings?.find(_temp);
      if (!warning) {
        t2 = undefined;
        break bb0;
      }
      t2 = {
        message: "This vault has a timelock below 3 days",
        level: apiWarningLevelToEnum(warning.level)
      };
    }
    $[2] = warnings;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shortTimelockWarning = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-secondary-foreground text-xs">Timelock duration</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== shortTimelockWarning) {
    t4 = shortTimelockWarning && <WarningIcon warnings={[shortTimelockWarning]} isSimple={true} />;
    $[5] = shortTimelockWarning;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== address || $[8] !== chainId || $[9] !== hasPendingTimelocks || $[10] !== pendingTimelocks || $[11] !== timelock) {
    t5 = hasPendingTimelocks && <VaultPendingTimelockInfo content={<div className="p-sm gap-sm flex flex-col"><span className="text-foreground text-sm">{formatPendingActionsLabel(pendingTimelocks.length)}</span>{pendingTimelocks.map((t6, index) => {
        const {
          decodedData,
          functionName,
          validAt
        } = t6;
        return <VaultPendingTimelocks chainId={chainId} key={`${functionName}-${index}`} decodedData={decodedData} functionName={functionName} validAt={validAt} address={address} currentTimelock={timelock} />;
      })}</div>}><Icon className="text-info-foreground" icon={<Timelock />} /></VaultPendingTimelockInfo>;
    $[7] = address;
    $[8] = chainId;
    $[9] = hasPendingTimelocks;
    $[10] = pendingTimelocks;
    $[11] = timelock;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <DataList.LeftItem>{t3}{t4}{t5}</DataList.LeftItem>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== timelockDuration) {
    t7 = <DataList.RightItem><span className="text-foreground text-xs">{timelockDuration}</span></DataList.RightItem>;
    $[16] = timelockDuration;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <DataList.Line>{t6}{t7}</DataList.Line>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export default memo(TimelockDurationDataLine);
function _temp(w) {
  return w.type === VaultWarningType.SHORT_TIMELOCK;
}